// Chroma-key compositor (ADR 0070). Runs entirely client-side on the Legend's
// machine: it takes the raw camera MediaStreamTrack + a background image, keys out
// the green screen in a WebGL fragment shader, composites the camera over the
// background, and exposes the result as a canvas.captureStream() MediaStream. The
// Legend's Go-Live code publishes / replaces its video track with this stream, so
// the keyed result is baked into the Legend feed BEFORE it reaches Program/Fans —
// no server-side compositing, no viewer-side work.
//
// WebGL only; if a context can't be created the caller falls back to the raw
// camera (compositing is best-effort, never a hard dependency for going live).

import { startFrameClock, type FrameClock } from './workerClock'

/** How the background image is fitted to the camera frame. */
export type FitMode = 'cover' | 'contain' | 'stretch'

/**
 * Compositor is the common shape of the two background compositors — the WebGL
 * chroma keyer (ChromaCompositor) and the ML segmenter (MLCompositor, mlSegment.ts).
 * The Go-Live reconcile effect picks one by program.chroma.mode and drives it the
 * same way: set the background + params, start() from the raw camera track to get a
 * composited MediaStream, stop() to release it. start() may be async (the ML path
 * lazy-loads its model), so callers await it.
 */
export interface Compositor {
  setBackground(img: HTMLImageElement | null): void
  setParams(p: ChromaParams): void
  /** Swap the INPUT camera track in place for a live camera switch (LSC-613) —
   *  the published composited stream is untouched, so fans/Director see no drop. */
  setSource(sourceTrack: MediaStreamTrack): void
  start(sourceTrack: MediaStreamTrack, fps?: number): MediaStream | Promise<MediaStream>
  stop(): void
  readonly stream: MediaStream | null
}

/** Compute the background UV/scale fit factors (shared by both compositors). */
export function fitScale(fit: FitMode, bgW: number, bgH: number, frameW: number, frameH: number): [number, number] {
  if (fit === 'stretch' || !bgW || !bgH || !frameW || !frameH) return [1, 1]
  const bgAspect = bgW / bgH
  const frameAspect = frameW / frameH
  if (fit === 'contain') {
    return bgAspect > frameAspect ? [1, bgAspect / frameAspect] : [frameAspect / bgAspect, 1]
  }
  // cover
  return bgAspect > frameAspect ? [frameAspect / bgAspect, 1] : [1, bgAspect / frameAspect]
}

export type ChromaParams = {
  color?: string // key color hex, e.g. "#00b140" (default green screen)
  similarity?: number // 0..1 — how close a pixel's chroma must be to the key to drop out
  smoothness?: number // 0..1 — soft edge width
  spill?: number // 0..1 — green-spill suppression on the kept subject
  fit?: FitMode // how the background fills the frame (default cover)
}

export const DEFAULT_CHROMA: Required<ChromaParams> = {
  color: '#00b140',
  similarity: 0.4,
  smoothness: 0.08,
  spill: 0.5, // targeted key-hue despill (ADR 0070) — a moderate default
  fit: 'cover',
}

const VERT = `
attribute vec2 aPos;
varying vec2 vUv;
void main() {
  // aPos is a fullscreen quad in clip space; map to [0,1] uv, flipping Y so the
  // (top-left-origin) video/image samples upright.
  vUv = vec2((aPos.x + 1.0) * 0.5, 1.0 - (aPos.y + 1.0) * 0.5);
  gl_Position = vec4(aPos, 0.0, 1.0);
}
`

const FRAG = `
precision mediump float;
uniform sampler2D uCamera;
uniform sampler2D uBg;
uniform vec3 uKey;
uniform float uSimilarity;
uniform float uSmoothness;
uniform float uSpill;
// Background fit: scale applied to (uv-0.5) when sampling the background, so
// cover crops (scale<=1, always in-range) and contain letterboxes (scale>=1,
// out-of-range → the letterbox fill). stretch = (1,1).
uniform vec2 uBgScale;
varying vec2 vUv;

// BT.601 RGB<->YCbCr. Keying on the (Cb,Cr) chroma plane is brightness/lighting
// tolerant; reconstructing RGB lets us despill in chroma space.
vec3 rgb2ycbcr(vec3 c) {
  return vec3(
    dot(c, vec3(0.299, 0.587, 0.114)),
    dot(c, vec3(-0.168736, -0.331264, 0.5)),
    dot(c, vec3(0.5, -0.418688, -0.081312))
  );
}
vec3 ycbcr2rgb(vec3 v) {
  return vec3(
    v.x + 1.402 * v.z,
    v.x - 0.344136 * v.y - 0.714136 * v.z,
    v.x + 1.772 * v.y
  );
}

void main() {
  vec4 cam = texture2D(uCamera, vUv);
  vec2 bgUv = (vUv - 0.5) * uBgScale + 0.5;
  vec3 bg = (bgUv.x < 0.0 || bgUv.x > 1.0 || bgUv.y < 0.0 || bgUv.y > 1.0)
    ? vec3(0.0)                    // contain letterbox
    : texture2D(uBg, bgUv).rgb;

  vec3 camYCC = rgb2ycbcr(cam.rgb);
  vec3 keyYCC = rgb2ycbcr(uKey);

  // Alpha from the chroma-plane distance to the key color (lighting-tolerant).
  float d = distance(camYCC.yz, keyYCC.yz);
  float alpha = smoothstep(uSimilarity, uSimilarity + uSmoothness, d);

  // Targeted despill: remove the component of the kept subject's chroma that
  // leans toward the KEY hue (projection onto the key's chroma direction). This
  // kills a green rim/fringe without desaturating the whole subject, and works
  // for any key color the eyedropper picks — not just green.
  float keyLen = length(keyYCC.yz);
  vec2 keyDir = keyLen > 1e-4 ? keyYCC.yz / keyLen : vec2(0.0);
  float proj = max(dot(camYCC.yz, keyDir), 0.0);
  vec2 despilledCC = camYCC.yz - keyDir * proj * uSpill;
  vec3 fg = clamp(ycbcr2rgb(vec3(camYCC.x, despilledCC)), 0.0, 1.0);

  gl_FragColor = vec4(mix(bg, fg, alpha), 1.0);
}
`

function hexToRgb(hex: string): [number, number, number] {
  const m = /^#?([0-9a-f]{6})$/i.exec(hex.trim())
  if (!m) return [0, 0.69, 0.25]
  const n = parseInt(m[1], 16)
  return [((n >> 16) & 255) / 255, ((n >> 8) & 255) / 255, (n & 255) / 255]
}

function compile(gl: WebGLRenderingContext, type: number, src: string): WebGLShader {
  const sh = gl.createShader(type)!
  gl.shaderSource(sh, src)
  gl.compileShader(sh)
  if (!gl.getShaderParameter(sh, gl.COMPILE_STATUS)) {
    const log = gl.getShaderInfoLog(sh)
    gl.deleteShader(sh)
    throw new Error('chroma shader: ' + log)
  }
  return sh
}

/**
 * ChromaCompositor keys `sourceTrack` over `background` and yields a composited
 * MediaStream via start(). Update the background/params live with setBackground /
 * setParams. Call stop() to release the GL context, RAF loop, and hidden video.
 */
// Absolute background-texture cap. The composite runs at camera resolution, so a
// >1920px background buys nothing, and 1920 is safely under even constrained GPU
// MAX_TEXTURE_SIZE limits. The effective cap is min(MAX_TEXTURE_SIZE, this) (LSC-623).
const BG_MAX_TEX = 1920

export class ChromaCompositor {
  private canvas: HTMLCanvasElement
  private gl: WebGLRenderingContext | null = null
  private video: HTMLVideoElement
  private clock: FrameClock | null = null
  private params: Required<ChromaParams> = { ...DEFAULT_CHROMA }
  private bgImage: HTMLImageElement | null = null
  private bgTex: WebGLTexture | null = null
  private camTex: WebGLTexture | null = null
  private uniforms: Record<string, WebGLUniformLocation | null> = {}
  private bgDirty = false
  // Runtime GPU texture ceiling (0 until start()); the background is clamped to
  // min(this, BG_MAX_TEX) before upload so an oversized texture can't silently fail
  // → black (LSC-623). bgScaleCanvas caches the downscale target.
  private maxTex = 0
  private bgScaleCanvas: HTMLCanvasElement | null = null
  stream: MediaStream | null = null

  constructor() {
    this.canvas = document.createElement('canvas')
    this.canvas.width = 1280
    this.canvas.height = 720
    this.video = document.createElement('video')
    this.video.muted = true
    this.video.playsInline = true
    this.video.autoplay = true
  }

  /** True if WebGL is available; callers fall back to the raw camera otherwise. */
  static supported(): boolean {
    try {
      const c = document.createElement('canvas')
      return !!(c.getContext('webgl') || c.getContext('experimental-webgl'))
    } catch {
      return false
    }
  }

  setParams(p: ChromaParams) {
    this.params = {
      color: p.color ?? this.params.color,
      similarity: p.similarity ?? this.params.similarity,
      smoothness: p.smoothness ?? this.params.smoothness,
      spill: p.spill ?? this.params.spill,
      fit: p.fit ?? this.params.fit,
    }
  }

  /** Swap the background image (already-loaded HTMLImageElement, or null to clear). */
  setBackground(img: HTMLImageElement | null) {
    this.bgImage = img
    this.bgDirty = true
  }

  /**
   * Begin compositing `sourceTrack` and return the composited MediaStream (30fps
   * canvas capture). Throws if WebGL can't initialize — callers should catch and
   * publish the raw camera instead.
   */
  start(sourceTrack: MediaStreamTrack, fps = 30): MediaStream {
    const gl = (this.canvas.getContext('webgl', { premultipliedAlpha: false }) ||
      this.canvas.getContext('experimental-webgl')) as WebGLRenderingContext | null
    if (!gl) throw new Error('WebGL unavailable')
    this.gl = gl
    this.maxTex = (gl.getParameter(gl.MAX_TEXTURE_SIZE) as number) || 0

    const prog = gl.createProgram()!
    gl.attachShader(prog, compile(gl, gl.VERTEX_SHADER, VERT))
    gl.attachShader(prog, compile(gl, gl.FRAGMENT_SHADER, FRAG))
    gl.linkProgram(prog)
    if (!gl.getProgramParameter(prog, gl.LINK_STATUS)) {
      throw new Error('chroma program link: ' + gl.getProgramInfoLog(prog))
    }
    gl.useProgram(prog)

    const buf = gl.createBuffer()
    gl.bindBuffer(gl.ARRAY_BUFFER, buf)
    gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([-1, -1, 1, -1, -1, 1, -1, 1, 1, -1, 1, 1]), gl.STATIC_DRAW)
    const aPos = gl.getAttribLocation(prog, 'aPos')
    gl.enableVertexAttribArray(aPos)
    gl.vertexAttribPointer(aPos, 2, gl.FLOAT, false, 0, 0)

    this.camTex = gl.createTexture()
    this.bgTex = gl.createTexture()
    for (const t of [this.camTex, this.bgTex]) {
      gl.bindTexture(gl.TEXTURE_2D, t)
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE)
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE)
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR)
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR)
    }
    gl.uniform1i(gl.getUniformLocation(prog, 'uCamera'), 0)
    gl.uniform1i(gl.getUniformLocation(prog, 'uBg'), 1)
    this.uniforms = {
      uKey: gl.getUniformLocation(prog, 'uKey'),
      uSimilarity: gl.getUniformLocation(prog, 'uSimilarity'),
      uSmoothness: gl.getUniformLocation(prog, 'uSmoothness'),
      uSpill: gl.getUniformLocation(prog, 'uSpill'),
      uBgScale: gl.getUniformLocation(prog, 'uBgScale'),
    }

    this.video.srcObject = new MediaStream([sourceTrack])
    void this.video.play().catch(() => {})

    this.stream = this.canvas.captureStream(fps)
    // Drive the compositor off a background-proof Worker clock, NOT rAF (ADR 0090/
    // 0102). rAF is PAUSED when the tab is backgrounded, and even a main-thread
    // setTimeout pump is CLAMPED to ~1s while hidden — both froze / stalled the
    // captured canvas track for EVERY fan the moment the Legend alt-tabbed to the
    // Director console / Slack. A dedicated Worker's timer is not throttled, so
    // startFrameClock keeps firing at `fps` while hidden and caps the work to the
    // published rate (no 2-5x display-refresh overdraw). It falls back to the ADR
    // 0090 self-correcting setTimeout pump if a Worker can't be created, and wraps
    // draw() in try/catch so a transient GL throw skips one frame, not the loop.
    this.clock = startFrameClock(fps, () => this.draw())
    return this.stream
  }

  // Swap the input camera in place (live camera switch, LSC-613). The render loop
  // samples this.video every tick, so pointing it at the new device's track changes
  // the composited output seamlessly — the captured canvas stream (the published
  // track) and the WebGL program are untouched, so there's no replaceTrack and no
  // drop for fans/Director.
  setSource(sourceTrack: MediaStreamTrack): void {
    this.video.srcObject = new MediaStream([sourceTrack])
    void this.video.play().catch(() => {})
  }

  // Clamp the background to a GPU-safe size for upload. A texture larger than the
  // runtime MAX_TEXTURE_SIZE fails to upload (silent GL INVALID_VALUE → a black
  // background), and the composite only ever runs at camera resolution, so a
  // >1920px texture buys nothing. Returns the image directly when it already fits,
  // else a cached, aspect-preserving downscale canvas (LSC-623).
  private bgUploadSource(): TexImageSource | null {
    const img = this.bgImage
    if (!img || !img.complete || !img.naturalWidth) return null
    const cap = Math.min(this.maxTex || 4096, BG_MAX_TEX)
    const w = img.naturalWidth
    const h = img.naturalHeight
    if (w <= cap && h <= cap) return img
    const scale = Math.min(cap / w, cap / h)
    const dw = Math.max(1, Math.round(w * scale))
    const dh = Math.max(1, Math.round(h * scale))
    let cv = this.bgScaleCanvas
    if (!cv) {
      cv = document.createElement('canvas')
      this.bgScaleCanvas = cv
    }
    if (cv.width !== dw || cv.height !== dh) {
      cv.width = dw
      cv.height = dh
    }
    const cx = cv.getContext('2d')
    if (!cx) return img
    cx.clearRect(0, 0, dw, dh)
    cx.drawImage(img, 0, 0, dw, dh)
    return cv
  }

  private draw() {
    const gl = this.gl
    if (!gl) return
    const vw = this.video.videoWidth
    const vh = this.video.videoHeight
    if (vw && vh && (this.canvas.width !== vw || this.canvas.height !== vh)) {
      this.canvas.width = vw
      this.canvas.height = vh
      gl.viewport(0, 0, vw, vh)
    }
    gl.pixelStorei(gl.UNPACK_FLIP_Y_WEBGL, false)

    // Camera → texture unit 0 (skip until the video has real frames).
    if (this.video.readyState >= 2 && vw && vh) {
      gl.activeTexture(gl.TEXTURE0)
      gl.bindTexture(gl.TEXTURE_2D, this.camTex)
      try {
        gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, gl.RGBA, gl.UNSIGNED_BYTE, this.video)
      } catch {
        return
      }
    }
    // Background → texture unit 1 (re-upload only when it changes). Clamp to a
    // GPU-safe size and fail soft: an oversized texture raises a silent GL
    // INVALID_VALUE (→ a black background), so drain prior errors, upload the clamped
    // source, and fall back to the neutral dark frame if it didn't take (LSC-623).
    if (this.bgDirty) {
      gl.activeTexture(gl.TEXTURE1)
      gl.bindTexture(gl.TEXTURE_2D, this.bgTex)
      const src = this.bgUploadSource()
      let ok = false
      if (src) {
        try {
          while (gl.getError() !== gl.NO_ERROR) {
            /* drain any pre-existing error so the post-upload check is ours */
          }
          gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, gl.RGBA, gl.UNSIGNED_BYTE, src)
          ok = gl.getError() === gl.NO_ERROR
        } catch {
          ok = false
        }
      }
      if (!ok) {
        // No image (yet), or the upload failed: a neutral dark frame so the subject still shows.
        gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, 1, 1, 0, gl.RGBA, gl.UNSIGNED_BYTE, new Uint8Array([20, 24, 28, 255]))
      }
      this.bgDirty = false
    }

    const [r, g, b] = hexToRgb(this.params.color)
    gl.uniform3f(this.uniforms.uKey, r, g, b)
    gl.uniform1f(this.uniforms.uSimilarity, this.params.similarity)
    gl.uniform1f(this.uniforms.uSmoothness, this.params.smoothness)
    gl.uniform1f(this.uniforms.uSpill, this.params.spill)

    // Background fit → the UV scale the shader applies to (uv-0.5). cover: sample
    // a sub-window (<=1) so the image fills + crops; contain: a super-window (>=1)
    // so it fits + letterboxes; stretch: (1,1).
    const [sx, sy] = fitScale(
      this.params.fit,
      this.bgImage?.naturalWidth ?? 0,
      this.bgImage?.naturalHeight ?? 0,
      this.canvas.width,
      this.canvas.height,
    )
    gl.uniform2f(this.uniforms.uBgScale, sx, sy)

    gl.activeTexture(gl.TEXTURE0)
    gl.bindTexture(gl.TEXTURE_2D, this.camTex)
    gl.activeTexture(gl.TEXTURE1)
    gl.bindTexture(gl.TEXTURE_2D, this.bgTex)
    gl.drawArrays(gl.TRIANGLES, 0, 6)
  }

  stop() {
    this.clock?.stop()
    this.clock = null
    this.stream?.getTracks().forEach((t) => t.stop())
    this.stream = null
    try {
      this.video.pause()
      this.video.srcObject = null
    } catch {
      /* ignore */
    }
    const gl = this.gl
    if (gl) {
      gl.deleteTexture(this.camTex)
      gl.deleteTexture(this.bgTex)
      const ext = gl.getExtension('WEBGL_lose_context')
      ext?.loseContext()
    }
    this.gl = null
  }
}

/**
 * Sample the pixel color (as #rrggbb) at a client point over a <video> displayed
 * with `object-fit: cover` — used by the eyedropper on both the Legend preview
 * and the Director's on-program tile. Returns null if unavailable (no frame yet /
 * read blocked). Maps the click through the cover crop to source pixels.
 */
export function sampleVideoColor(el: HTMLVideoElement, clientX: number, clientY: number): string | null {
  const vw = el.videoWidth
  const vh = el.videoHeight
  if (!vw || !vh) return null
  const rect = el.getBoundingClientRect()
  const scale = Math.max(rect.width / vw, rect.height / vh) // cover
  const dispW = vw * scale
  const dispH = vh * scale
  const sx = Math.round((clientX - rect.left - (rect.width - dispW) / 2) / scale)
  const sy = Math.round((clientY - rect.top - (rect.height - dispH) / 2) / scale)
  const cx = Math.min(vw - 1, Math.max(0, sx))
  const cy = Math.min(vh - 1, Math.max(0, sy))
  try {
    const canvas = document.createElement('canvas')
    canvas.width = vw
    canvas.height = vh
    const ctx = canvas.getContext('2d')
    if (!ctx) return null
    ctx.drawImage(el, 0, 0, vw, vh)
    const d = ctx.getImageData(cx, cy, 1, 1).data
    return '#' + [d[0], d[1], d[2]].map((n) => n.toString(16).padStart(2, '0')).join('')
  } catch {
    return null
  }
}

/**
 * Load an image URL as an HTMLImageElement usable as a WebGL texture (CORS-clean).
 *
 * The primary path is a crossOrigin='anonymous' <img> — required so the composited
 * canvas can be captured and published (a tainted canvas can't be captured). But that
 * path FAILS, firing onerror, if the request is treated as cross-origin and the server
 * omits Access-Control-Allow-Origin, OR if the browser reuses a cache entry first
 * fetched without CORS — and a thrown loadImage makes the whole chroma compositor
 * silently fall back to the RAW camera (the green screen leaks on air, LSC-623). The
 * fallback fetches the bytes same-origin and loads them through a blob: URL, which
 * inherits the document origin, is never tainted, and needs no CORS header at all —
 * robust to the crossOrigin/cache interaction and to a missing ACAO.
 */
/**
 * Diagnostic detail from the blob-fallback RESCUE path (LSC-623): the `/uploads` response
 * the browser actually received, so a recovered background load explains WHY the crossOrigin
 * path failed without the operator's machine — did the server send `Access-Control-Allow-Origin`,
 * was the request cross-origin, how did the browser classify the response.
 */
export interface BgLoadRecovery {
  status: number
  /** `Access-Control-Allow-Origin` on the `/uploads` response, or null if the server omitted it. */
  acao: string | null
  /** `Response.type`: `basic` (same-origin) | `cors` | `opaque` (cross-origin, no CORS) | … */
  respType: string
  contentType: string | null
  /** Was the background URL cross-origin relative to the operator's document? */
  crossOrigin: boolean
  /** The operator's document origin (the cave host). */
  origin: string
}

export function loadImage(url: string, onRecovered?: (r: BgLoadRecovery) => void): Promise<HTMLImageElement> {
  // Primary: the crossOrigin <img>. On failure, the same-origin blob fallback rescues it and,
  // if it succeeds, reports WHY via onRecovered (the actual /uploads response) — so the
  // resilience is self-explaining rather than a silent safety net (LSC-623). A clean crossOrigin
  // load never fetches or reports.
  return loadImageEl(url, true).catch(() => loadImageViaBlob(url, onRecovered))
}

function docOrigin(): string {
  try {
    return typeof location !== 'undefined' ? location.origin : ''
  } catch {
    return ''
  }
}

function isCrossOrigin(url: string): boolean {
  try {
    if (typeof location === 'undefined') return false
    return new URL(url, location.href).origin !== location.origin
  } catch {
    return false
  }
}

function loadImageEl(src: string, cors: boolean): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image()
    if (cors) img.crossOrigin = 'anonymous'
    img.onload = () => resolve(img)
    img.onerror = () => reject(new Error('image load failed: ' + src))
    img.src = src
  })
}

async function loadImageViaBlob(url: string, onRecovered?: (r: BgLoadRecovery) => void): Promise<HTMLImageElement> {
  const resp = await fetch(url, { credentials: 'same-origin' })
  if (!resp.ok) throw new Error('background fetch ' + resp.status + ': ' + url)
  const obj = URL.createObjectURL(await resp.blob())
  try {
    const img = await loadImageEl(obj, false)
    try {
      // The rescue succeeded — report the actual /uploads response so we can tell CORS/cache
      // (no/absent ACAO, opaque/cross-origin) from a GPU-decode cause next time (LSC-623).
      onRecovered?.({
        status: resp.status,
        acao: resp.headers.get('access-control-allow-origin'),
        respType: resp.type,
        contentType: resp.headers.get('content-type'),
        crossOrigin: isCrossOrigin(url),
        origin: docOrigin(),
      })
    } catch {
      /* diagnostics must never break image loading */
    }
    return img
  } finally {
    // The decoded bitmap is retained by the element after onload resolves; release
    // the object URL shortly after so it can't leak.
    setTimeout(() => URL.revokeObjectURL(obj), 15000)
  }
}

/**
 * WebGL diagnostics for support triage (LSC-623). Captures the renderer's texture
 * ceiling + the GPU string (hardware vs software/SwiftShader) so a chroma fallback
 * records WHY — the single data point that distinguishes a dropped GPU, a too-large
 * texture, and a load failure after the fact. Ids/strings only, no PII.
 */
export function webglDiagnostics(): { maxTextureSize: number; renderer: string } {
  try {
    const c = document.createElement('canvas')
    const gl = (c.getContext('webgl') || c.getContext('experimental-webgl')) as WebGLRenderingContext | null
    if (!gl) return { maxTextureSize: 0, renderer: 'no-webgl' }
    const dbg = gl.getExtension('WEBGL_debug_renderer_info')
    const renderer = dbg ? String(gl.getParameter(dbg.UNMASKED_RENDERER_WEBGL)) : 'masked'
    return { maxTextureSize: (gl.getParameter(gl.MAX_TEXTURE_SIZE) as number) || 0, renderer }
  } catch {
    return { maxTextureSize: 0, renderer: 'error' }
  }
}

/**
 * Classify a `WEBGL_debug_renderer_info` UNMASKED_RENDERER_WEBGL string as a
 * software (CPU) rasterizer. When a browser has no GPU access — hardware
 * acceleration turned off, or the GPU process crashed and Chrome fell back to
 * "software GL" — WebGL is served by SwiftShader (Chrome), llvmpipe (Mesa), or the
 * "Microsoft Basic Render Driver". Chroma keying still nominally runs there, but it
 * is slow/fragile and one crash away from Chrome disabling WebGL entirely
 * (`chrome://gpu` → "Disabled Features: all"), which is exactly the state that leaked
 * Curt's raw green screen on air during World Series Game 3 (LSC-623). Pure +
 * exported so it is unit-testable without a real GL context.
 */
export function isSoftwareRenderer(renderer: string): boolean {
  return /swiftshader|software|llvmpipe|basic render|microsoft basic|softpipe/i.test(renderer || '')
}

export type GpuHealth = 'ok' | 'software' | 'unavailable'

/**
 * The machine's GPU posture for the chroma compositor (LSC-623 follow-up), so the
 * operator can be warned BEFORE a silent raw-green-screen leak:
 *   - `unavailable` — no WebGL context at all (GPU process dead / WebGL blocklisted).
 *     The compositor can't build; the raw camera (green screen) would go on air.
 *   - `software`    — WebGL on a CPU rasterizer (hardware acceleration off). Keying
 *     works but may stutter/drop, and is one crash from `unavailable`.
 *   - `ok`          — hardware-accelerated, or the renderer string is masked (the
 *     debug extension is unavailable), in which case we don't false-alarm.
 */
export function gpuHealth(): { health: GpuHealth; renderer: string; maxTextureSize: number } {
  const d = webglDiagnostics()
  if (!d.maxTextureSize && (d.renderer === 'no-webgl' || d.renderer === 'error')) {
    return { health: 'unavailable', renderer: d.renderer, maxTextureSize: 0 }
  }
  if (isSoftwareRenderer(d.renderer)) return { health: 'software', renderer: d.renderer, maxTextureSize: d.maxTextureSize }
  return { health: 'ok', renderer: d.renderer, maxTextureSize: d.maxTextureSize }
}

// chooseRawCameraSource picks the raw camera track to (re)build a Legend
// compositor from. On a MODE SWITCH the prior compositor's source (still live — a
// compositor stop() only stops its own canvas track, never the camera) MUST be
// preferred over the LiveKit publication's current track: teardownChroma restores
// the publication to raw with a fire-and-forget (un-awaited) replaceTrack, so
// immediately after teardown the publication still transiently reports the OLD
// composited track that was just stopped. Feeding that dead track to the new
// compositor is exactly the frozen-video-across-Legend/Director/Fan bug that only
// a Legend restart cleared (LSC-329). A FRESH build has no prior source, so the
// publication's current track (the genuine raw camera — nothing has replaced it
// yet) is used and captured.
export function chooseRawCameraSource<T extends { readyState?: string }>(
  priorRaw: T | null | undefined,
  publicationTrack: T | null | undefined,
): T | null {
  if (priorRaw && priorRaw.readyState !== 'ended') return priorRaw
  return publicationTrack ?? null
}

// needsBackgroundRefetch answers whether the Legend should re-fetch its cave
// background library because an inbound program.chroma references a backgroundId
// it doesn't have. On a FRESH Cave the Director can select or upload a background
// the Legend fetched-empty at mount; without a refetch the Legend can't resolve
// the id, so nothing composites until the Legend seeds a background locally
// (LSC-339). Guarded by the last id tried so a genuinely-missing id (e.g. deleted)
// triggers exactly one refetch, never a loop.
export function needsBackgroundRefetch(
  wantId: string | undefined | null,
  knownIds: readonly string[],
  lastTriedId: string | null,
): boolean {
  if (!wantId) return false
  if (knownIds.includes(wantId)) return false
  return lastTriedId !== wantId
}
